import type { Collab } from "@cabal/shared/domain/session/collab/types.js";
import type {
  ProductDetailResponse,
  ProductSummary,
} from "@cabal/shared/domain/session/product/types.js";
import type {
  VersionDetailResponse,
  VersionSummary,
} from "@cabal/shared/domain/session/version/types.js";
import type { AttachedFileView } from "@cabal/shared/domain/upload/types.js";

interface AttachedFileIndexSources {
  chatMessages: Record<string, Collab>;
  collabs: Record<string, Collab>;
  productDetailsById: Record<string, ProductDetailResponse>;
  products: Record<string, ProductSummary>;
  versionDetailsById: Record<string, VersionDetailResponse>;
  versions: Record<string, VersionSummary>;
}

interface AttachedFileContainer {
  attachedFiles?: AttachedFileView[];
}

export function selectAttachedFiles(
  sources: AttachedFileIndexSources,
): Record<string, AttachedFileView> {
  return selectAttachedFileGroups(sources)
    .flat()
    .reduce<Record<string, AttachedFileView>>((filesById, file) => {
      filesById[file.id] = file;
      return filesById;
    }, {});
}

function selectAttachedFileGroups(
  sources: AttachedFileIndexSources,
): AttachedFileView[][] {
  return [
    ...selectContainerFileGroups(Object.values(sources.products)),
    ...selectContainerFileGroups(Object.values(sources.versions)),
    ...selectProductDetailFileGroups(Object.values(sources.productDetailsById)),
    ...selectContainerFileGroups(Object.values(sources.versionDetailsById)),
    ...selectContainerFileGroups(Object.values(sources.collabs)),
    ...selectContainerFileGroups(Object.values(sources.chatMessages)),
  ];
}

function selectContainerFileGroups(
  containers: AttachedFileContainer[],
): AttachedFileView[][] {
  return containers.map((container) => container.attachedFiles ?? []);
}

function selectProductDetailFileGroups(
  productDetails: ProductDetailResponse[],
): AttachedFileView[][] {
  return productDetails.flatMap((detail) => [
    detail.attachedFiles ?? [],
    detail.latestVersion?.attachedFiles ?? [],
    ...selectContainerFileGroups(detail.children),
  ]);
}
